import React, { useCallback, useEffect, useState } from "react";
import {
  deleteEnrollmentPhoto,
  getEnrollmentPhotoErrorMessage,
  getEnrollmentPhotoMetadata,
  replaceEnrollmentPhoto,
} from "../api/enrollmentPhoto";
import type { EnrollmentPhotoMetadata } from "../interfaces/SelfieEnrollment";
import { useBlobImageUrl } from "../hooks/useBlobImageUrl";
import { useSiteContext } from "../SiteContext";
import "./EnrollmentPhotoModal.css";

interface EnrollmentPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  employeeLabel: string;
  canEdit: boolean;
}

const EnrollmentPhotoModal: React.FC<EnrollmentPhotoModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  employeeLabel,
  canEdit,
}) => {
  const { createPopup, translator } = useSiteContext();
  const [metadata, setMetadata] = useState<EnrollmentPhotoMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { blobUrl, loading: imageLoading, error: imageError } = useBlobImageUrl(
    metadata?.hasEnrollmentPhoto ? metadata.imageUrl : null
  );

  const refreshMetadata = useCallback(async () => {
    setMetadataLoading(true);
    setLoadError(null);
    try {
      const m = await getEnrollmentPhotoMetadata(employeeId);
      setMetadata(m);
    } catch (err) {
      setLoadError(getEnrollmentPhotoErrorMessage(err));
    } finally {
      setMetadataLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!isOpen || !employeeId) return;
    refreshMetadata();
  }, [isOpen, employeeId, refreshMetadata]);

  useEffect(() => {
    if (!isOpen) {
      setMetadata(null);
      setLoadError(null);
    }
  }, [isOpen]);

  const handleReplace: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      await replaceEnrollmentPhoto(employeeId, file);
      await refreshMetadata();
    } catch (err) {
      createPopup(translator.translate("ExportError"), getEnrollmentPhotoErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(translator.translate("enrollmentPhotoDeleteConfirm"))
    ) {
      return;
    }
    try {
      await deleteEnrollmentPhoto(employeeId);
      await refreshMetadata();
    } catch (err) {
      createPopup(translator.translate("ExportError"), getEnrollmentPhotoErrorMessage(err));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="enrollment-photo-modal-overlay" onClick={onClose}>
      <div className="enrollment-photo-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="enrollment-photo-modal-header">
          <h2>{translator.translate("enrollmentPhotoTitle")}</h2>
          <button type="button" onClick={onClose}>
            {translator.translate("Close")}
          </button>
        </div>
        <div className="enrollment-photo-modal-body">
          <p style={{ marginTop: 0 }}>
            <strong>{employeeLabel}</strong>
          </p>

          {loadError && <div className="enrollment-photo-modal-error">{loadError}</div>}

          {metadataLoading && !metadata && <p>{translator.translate("LoadingData")}</p>}

          {metadata && (
            <>
              <div className="enrollment-photo-modal-meta">
                <div>
                  {translator.translate("EmployeeID")}{" "}
                  {metadata.altEmployeeID} ({metadata.employeeID})
                </div>
                <div>
                  {metadata.firstName} {metadata.lastName}
                </div>
                <div>
                  {translator.translate("enrollmentPhotoIsEnrolled")}:{" "}
                  {metadata.isEnrolled
                    ? translator.translate("enrollmentPhotoYes")
                    : translator.translate("enrollmentPhotoNo")}
                </div>
                <div>
                  {translator.translate("enrollmentPhotoHasPhoto")}:{" "}
                  {metadata.hasEnrollmentPhoto
                    ? translator.translate("enrollmentPhotoYes")
                    : translator.translate("enrollmentPhotoNo")}
                </div>
                <div>
                  {translator.translate("enrollmentPhotoEnrolledAt")}:{" "}
                  {metadata.enrolledAt
                    ? new Date(metadata.enrolledAt).toLocaleString()
                    : "—"}
                </div>
              </div>

              <div className="enrollment-photo-modal-preview">
                {metadata.hasEnrollmentPhoto ? (
                  imageLoading || metadataLoading ? (
                    <span>{translator.translate("LoadingData")}</span>
                  ) : imageError ? (
                    <span className="enrollment-photo-modal-error">{imageError}</span>
                  ) : blobUrl ? (
                    <img src={blobUrl} alt="" />
                  ) : (
                    <span>{translator.translate("enrollmentPhotoNoPhoto")}</span>
                  )
                ) : (
                  <span>{translator.translate("enrollmentPhotoNoPhoto")}</span>
                )}
              </div>

              {canEdit && (
                <div className="enrollment-photo-modal-actions">
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,image/jpeg"
                      style={{ display: "none" }}
                      onChange={handleReplace}
                    />
                    <span style={{ padding: "8px 14px", border: "1px solid #cbd5e0", borderRadius: 6 }}>
                      {translator.translate("enrollmentPhotoReplace")}
                    </span>
                  </label>
                  {metadata.hasEnrollmentPhoto && (
                    <button type="button" className="enrollment-photo-delete" onClick={handleDelete}>
                      {translator.translate("enrollmentPhotoDelete")}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentPhotoModal;
